import { useCallback } from 'react';
import { openDbManagerQueryTab } from '../store/tabsStore';
import {
  listLiveDbConnections,
  listDbCapableExtensionIds,
  readSavedDbConnection,
  listSavedDbConnections,
  isDbConnectionLive,
  connectDbFromSavedConfig,
} from '../extensions/capabilities/db.js';
import { createAiDbRun } from '../services/aiExtensionBridges';
import { genId } from './aiExtensionHelpers';
import { fetchDbSchema } from './aiDbSchema';

/** DB Manager tools for the assistant: list connections, read a schema, run SQL. */
export function useAiDbActions({
  navigateToPanel,
}) {
  // ── DB Manager ────────────────────────────────────────────────────────────

  // Which enabled db-capable extension owns this connId (live first, then saved).
  const resolveDbExtension = useCallback(async (connId) => {
    const live = listLiveDbConnections().find((c) => c.connId === connId);
    if (live) return live.extensionId;
    for (const extId of listDbCapableExtensionIds()) {
      const saved = await readSavedDbConnection(extId, connId).catch(() => null);
      if (saved) return extId;
    }
    return null;
  }, []);

  const dbListConnections = useCallback(async () => {
    const live = listLiveDbConnections();
    const liveKey = new Set(live.map((c) => `${c.extensionId}\u0000${c.connId}`));
    const connections = [];
    for (const extId of listDbCapableExtensionIds()) {
      const saved = await listSavedDbConnections(extId).catch(() => []);
      for (const c of saved) {
        connections.push({
          conn_id: c.id,
          // Never the host or the file path (owner rule, 17.09.2026: from the
          // Vault an agent sees the NAME of an entry, nothing else). This
          // fallback chain used to end in `c.host || c.path`, so an UNNAMED
          // connection handed the model its server address — or, for SQLite, a
          // path with the OS user name in it — as its "name".
          name: c.name || c.database || `Unnamed ${c.engine || 'database'} connection`,
          engine: c.engine,
          database: c.database || null,
          live: liveKey.has(`${extId}\u0000${c.id}`),
        });
      }
    }
    return { connections };
  }, []);

  const ensureLive = useCallback(async (extId, connId) => {
    if (isDbConnectionLive(extId, connId)) return true;
    // An AI tool call is not a click: a locked Vault answers vault_locked.
    await connectDbFromSavedConfig(extId, connId, { interactive: false });
    return isDbConnectionLive(extId, connId);
  }, []);

  const dbGetSchema = useCallback(async (input) => {
    const connId = String(input.conn_id);
    const extId = await resolveDbExtension(connId);
    if (!extId) return { error: 'Connection not found' };
    const saved = await readSavedDbConnection(extId, connId).catch(() => null);
    const engine = (saved && saved.engine) || 'mysql';
    try {
      await ensureLive(extId, connId);
    } catch (err) {
      return { error: `Could not connect: ${err.message}` };
    }
    const dbName = input.database || (engine === 'postgres' ? (saved?.database || null) : (saved?.database || null));
    try {
      const schema = await fetchDbSchema(engine, extId, connId, dbName);
      return schema; // { tables:[...], views:[...] } — never any row data
    } catch (err) {
      return { error: `Schema read failed: ${err.message}` };
    }
  }, [resolveDbExtension, ensureLive]);

  const dbRunSql = useCallback(async (input, { interactive } = {}) => {
    const connId = String(input.conn_id);
    const sql = input.sql;
    if (!sql) return { error: 'sql is required' };
    const extId = await resolveDbExtension(connId);
    if (!extId) return { error: 'Connection not found' };
    const saved = await readSavedDbConnection(extId, connId).catch(() => null);
    const engine = (saved && saved.engine) || 'mysql';
    try {
      await ensureLive(extId, connId);
    } catch (err) {
      return { error: `Could not connect: ${err.message}` };
    }
    // Always run through the DB Manager grid so the rows render there and never
    // reach the model. navigateToPanel switches the app to DB Manager.
    navigateToPanel('db-manager.panel');
    const aiRunId = genId();
    const dbName = input.database || saved?.database || null;
    openDbManagerQueryTab({
      extensionId: extId,
      connId,
      engine,
      dbName,
      sql,
      autoRun: true,
      aiRunId,
    });
    // Resolves (metadata only) when DbManagerResultsTab finishes the auto-run.
    return createAiDbRun(aiRunId);
  }, [resolveDbExtension, ensureLive, navigateToPanel]);

  return { dbListConnections, dbGetSchema, dbRunSql };
}
